import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaHandPointLeft, FaHandPointRight } from "react-icons/fa";
import styles from "./MicTapTimeoutNudge.module.css";
import { getBestMicAnchorClientRect, getMicTapArrowFallbackRect } from "../../utils/micTapNudge";

function useMicAnchorRect(visible) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!visible) {
      setRect(null);
      return;
    }
    const best = getBestMicAnchorClientRect();
    setRect(best ?? getMicTapArrowFallbackRect());
  }, [visible]);

  useLayoutEffect(() => {
    if (!visible) {
      setRect(null);
      return;
    }
    measure();
  }, [visible, measure]);

  useEffect(() => {
    if (!visible) return;
    measure();
    const t = window.setTimeout(measure, 120);
    const t2 = window.setTimeout(measure, 320);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [visible, measure]);

  return rect;
}

export default function MicTapTimeoutNudge({ visible, onContinue }) {
  const micRect = useMicAnchorRect(visible);
  const arrowRect = visible ? (micRect ?? getMicTapArrowFallbackRect()) : null;

  const arrowSize = 96;
  const arrowHalf = arrowSize / 2;
  const arrowPlacement = useMemo(() => {
    if (arrowRect == null) return null;
    const gap = 14;
    const vw = typeof window !== "undefined" ? window.innerWidth : 400;
    const vh = typeof window !== "undefined" ? window.innerHeight : 700;
    const spaceLeft = arrowRect.left - gap;
    const spaceRight = vw - arrowRect.right - gap;
    const fitsLeft = spaceLeft >= arrowSize;
    const fitsRight = spaceRight >= arrowSize;
    let pointRight;
    if (fitsLeft && fitsRight) {
      pointRight = spaceLeft >= spaceRight;
    } else if (fitsLeft) {
      pointRight = true;
    } else if (fitsRight) {
      pointRight = false;
    } else {
      pointRight = spaceLeft >= spaceRight;
    }
    let left = pointRight ? arrowRect.left - arrowSize - gap : arrowRect.right + gap;
    const micMidY = arrowRect.top + arrowRect.height / 2;
    const verticalOffsetDown = 14;
    let top = micMidY - arrowHalf + verticalOffsetDown;
    top = Math.max(8, Math.min(top, vh - arrowSize - 8));
    left = Math.max(8, Math.min(left, vw - arrowSize - 8));
    return { left, top, pointRight };
  }, [arrowRect, arrowSize, arrowHalf]);

  const arrowStyle =
    arrowPlacement == null
      ? { display: "none" }
      : {
        display: "flex",
        position: "fixed",
        width: arrowSize,
        height: arrowSize,
        left: arrowPlacement.left,
        top: arrowPlacement.top,
      };

  const node = (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            key="mic-tap-nudge-overlay"
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => onContinue?.()}
            role="presentation"
          >
            <motion.div
              className={styles.card}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <p className={styles.title}>Time ran out.</p>
              <p className={styles.sub}>Tap the mic before you speak.</p>
              <div className={styles.btnRow}>
                <motion.button
                  type="button"
                  className={styles.btn}
                  onClick={() => onContinue?.()}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 550, damping: 22 }}
                >
                  OK
                </motion.button>
              </div>
            </motion.div>
          </motion.div>

          {arrowPlacement != null && (
            <motion.div
              key="mic-tap-nudge-hand"
              className={styles.handWrap}
              style={arrowStyle}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.08, type: "spring", stiffness: 420, damping: 22 }}
            >
              <motion.div
                className={styles.handBounce}
                animate={
                  arrowPlacement.pointRight
                    ? { x: [0, 10, 0] }
                    : { x: [0, -10, 0] }
                }
                transition={{ duration: 1.05, repeat: Infinity, ease: "easeInOut" }}
              >
                {arrowPlacement.pointRight ? (
                  <FaHandPointRight className={styles.handIcon} aria-hidden />
                ) : (
                  <FaHandPointLeft className={styles.handIcon} aria-hidden />
                )}
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
